// Wires the zustand session store to IndexedDB. Every relevant mutation
// triggers a debounced save; on mount we peek any stored session for the
// current tool/session-folder and return a gate state so the UI can
// prompt Continue / Start Fresh before hydration. Empty sessions (no name,
// no blocks/takes) are treated as "nothing meaningful" and hydrate
// silently — the prompt only appears when the user had actually
// progressed past Welcome on a previous visit.
//
// TTS-only scope was retired with Phase 4.2 — the hook now saves/loads the
// full SessionState so STS and video phases inherit persistence for free.
//
// Seit 2026-09-24 (Audit M8/M9):
//   - Ein Tab pro Session (Web Locks, lib/tabLock.ts). Der zweite Tab sieht
//     einen Hinweis und kann übernehmen; der erste wird dann read-only.
//   - Hydrate fail-closed: scheitert das Laden, gibt es KEINE Saves (die
//     hätten den leeren Stand über die mehrtägige Session geschrieben),
//     sondern eine Fehleransicht mit Retry.
//   - Der Record wird erst im Debounce-Timer aus getState() gebaut; Audio-
//     Bytes liegen im eigenen IDB-Store (lib/sessionAudio.ts) und werden pro
//     Take genau einmal geschrieben.
//   - Flush bei visibilitychange:hidden / pagehide.
//   - Autosave-Fehler landen in usePersistenceStore (Banner), nicht nur in
//     der Konsole; `navigator.storage.persist()` wird einmal angefragt.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { SessionState } from '@shared/types';
import { useSessionStore, type SfxBed } from '@/stores/session';
import { useVideoStore } from '@/stores/video';
import { usePersistenceStore } from '@/stores/persistence';
import { buildSessionRecord, hasMeaningfulState, rehydrateSession } from '@/lib/sessionRecord';
import {
  clearSession,
  deleteAllSfxBeds,
  deleteSfxBeds,
  formatIDBError,
  getAllSfxBeds,
  loadSession,
  putSfxBeds,
  saveSessionRecord,
  type StoredSession,
} from '@/lib/indexeddb';
import { sfxBedsToStored, storedToSfxBeds } from '@/lib/sfxBedPersistence';
import {
  buildIndexMapping,
  combinedAssetIdOrder,
  remapSessionIndices,
} from '@/lib/sessionRemap';
import { clampSessionBlockVoices } from '@/lib/voiceMigration';
import { acquireTabLock, type TabLock } from '@/lib/tabLock';
import {
  applyTakeDurations,
  collectZeroDurationTakes,
  probeAudioDuration,
  type ZeroDurationTake,
} from '@/lib/repairTakeDurations';

const SAVE_DEBOUNCE_MS = 800;

/** Takes ohne Dauer nachvermessen (Audit M17) — asynchron nach dem Restore,
 *  Ergebnis wird über eine pure Funktion in den Store gepatcht. Fehler pro
 *  Take werden geschluckt (der Take bleibt dann bei 0, wie bisher). */
async function repairZeroDurations(): Promise<void> {
  const todo = collectZeroDurationTakes(useSessionStore.getState());
  if (todo.length === 0) return;
  const measured: Array<{ item: ZeroDurationTake; duration: number }> = [];
  for (const item of todo) {
    try {
      measured.push({ item, duration: await probeAudioDuration(item.buffer) });
    } catch (err) {
      console.warn('[session] take re-decode failed', item.takeId, err);
    }
  }
  if (measured.length === 0) return;
  useSessionStore.setState((s) => {
    const next = applyTakeDurations(s, measured);
    return {
      assetBlocks: next.assetBlocks,
      freeBlocks: next.freeBlocks,
      blocks: next.blocks,
    };
  });
}

/** Record + Audio-Map aus dem LIVE-Store bauen — erst im Timer, nicht im
 *  Subscribe-Callback (M8c: der Callback bekam einen Zwischenstand, der
 *  Timer schrieb ihn 800 ms später, obwohl der Store längst weiter war).
 *  Der Bau selbst ist rein: lib/sessionRecord.ts. */
function buildRecord(persistKey: string) {
  return buildSessionRecord(useSessionStore.getState(), useVideoStore.getState().durations, persistKey, new Date());
}

let storagePersistRequested = false;
/** Einmal pro Seitenladung um dauerhaften Speicher bitten — Chromium räumt
 *  „best-effort"-Speicher unter Druck ab, persistente Buckets nicht. */
function requestPersistentStorage(): void {
  if (storagePersistRequested) return;
  storagePersistRequested = true;
  try {
    void navigator.storage?.persist?.().catch(() => undefined);
  } catch { /* ignore */ }
}

export type SessionGate =
  | { kind: 'checking' }
  | { kind: 'hydrated' }
  | {
      kind: 'restore-prompt';
      sessionName: string;
      onContinue(): void;
      onStartFresh(): void;
    }
  /** Ein anderer Tab hält diese Session. `onTakeOver` übernimmt sie hier. */
  | { kind: 'locked'; onTakeOver(): void }
  /** Laden aus IndexedDB gescheitert — keine Saves, bis Retry gelingt. */
  | { kind: 'error'; message: string; onRetry(): void };

/** Subscribes to the session store and persists changes to IndexedDB.
 *  Returns a gate state describing whether the caller should show the
 *  restore-prompt modal, wait, or render the page normally.
 *
 *  `persistKey` is the IndexedDB key for this session — NOT necessarily the
 *  same as the session store's own `toolId` field (which stays the real
 *  project id for API/upload calls). For standard (non-multilingual)
 *  projects `persistKey === toolId`, byte-identical to pre-multilingual
 *  behavior. For multilingual projects the caller passes `${toolId}:${LANG}`
 *  so each language gets an isolated IndexedDB slot and languages can't
 *  clobber each other's session/beds. See `docs/decisions.md` 2026-07-30. */
export function useSessionPersistence(
  persistKey: string | null | undefined,
): SessionGate {
  // Gate saves until hydration finishes so the initial empty store state
  // can't overwrite the user's prior session before we read it. Also false
  // while another tab holds the lock or hydration failed (fail-closed).
  const hydratedRef = useRef(false);
  const lockRef = useRef<TabLock | null>(null);
  const [gate, setGate] = useState<SessionGate>({ kind: 'checking' });
  // Bumped by Retry / Take-over to re-run the mount effect.
  const [attempt, setAttempt] = useState(0);
  // Take-over steals the lock inside the next effect run.
  const stealRef = useRef(false);

  // Last-MIRRORED sfxBeds slice — reference-compared in the bed mirror so
  // only real bed changes hit IndexedDB, and the restore's own setState
  // doesn't echo straight back into a redundant write.
  const lastBedsRef = useRef<Record<number, SfxBed[]>>({});

  // ── Debounced autosave (record built in the timer from the live store) ──
  const timerRef = useRef<number | null>(null);
  const savingRef = useRef(false);
  const dirtyRef = useRef(false);

  const flush = useCallback(() => {
    if (!persistKey || !hydratedRef.current) return;
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (savingRef.current) {
      // Ein Save läuft — danach noch einmal (dirty bleibt gesetzt).
      dirtyRef.current = true;
      return;
    }
    dirtyRef.current = false;
    let built: ReturnType<typeof buildRecord>;
    try {
      built = buildRecord(persistKey);
    } catch (err) {
      usePersistenceStore.getState().setAutosaveError(formatIDBError(err));
      return;
    }
    savingRef.current = true;
    saveSessionRecord(built.record, built.audio)
      .then(() => usePersistenceStore.getState().markSaved())
      .catch((err) => {
        console.error('[session] autosave failed', err);
        usePersistenceStore.getState().setAutosaveError(formatIDBError(err));
      })
      .finally(() => {
        savingRef.current = false;
        if (dirtyRef.current) flush();
      });
  }, [persistKey]);

  const scheduleSave = useCallback(() => {
    if (!hydratedRef.current) return;
    dirtyRef.current = true;
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      flush();
    }, SAVE_DEBOUNCE_MS);
  }, [flush]);

  // Applies a stored session to the store. No-op if nothing to apply.
  const applyStored = useCallback(async (stored: StoredSession, session: SessionState, persistKey: string) => {
    let hydrated = rehydrateSession(session);
    const store = useSessionStore.getState();
    // Re-key the index-based maps if the admin changed the asset list while
    // this session was parked in IDB (insert/reorder/delete shifts every
    // combined index). Old records without assetIdOrder keep indices as-is.
    const newOrder = combinedAssetIdOrder(
      store.config?.assets,
      hydrated.customAssets ?? [],
    );
    const oldOrder = stored.assetIdOrder ?? null;
    // oldIdx → newIdx mapping, also used for the bed restore below. Null =
    // no remap possible (legacy record) → identity.
    const mapping = oldOrder ? buildIndexMapping(oldOrder, newOrder) : null;
    if (oldOrder) {
      hydrated = remapSessionIndices(hydrated, oldOrder, newOrder);
    }
    // Defensive clamp regardless of remap: a currentAssetIdx pointing past
    // the (possibly shrunken) asset list would leave activeAsset undefined.
    if (hydrated.currentAssetIdx >= newOrder.length) {
      hydrated = {
        ...hydrated,
        currentAssetIdx: 0,
        blocks: hydrated.assetBlocks[0] ?? [],
      };
    }
    // Hat der Admin zwischenzeitlich eine Voice getauscht, tragen die
    // geparkten Blöcke noch die alte ID — auf die erlaubten Voices des
    // jeweiligen Assets kappen (Audit M10). Reihenfolge = kombinierte Liste
    // Config-Assets + Custom-Assets, dieselbe wie der Index-Remap oben.
    const mergedAssets = [...(store.config?.assets ?? []), ...(hydrated.customAssets ?? [])];
    hydrated = clampSessionBlockVoices(hydrated, mergedAssets);
    useSessionStore.setState({
      ...hydrated,
      toolId: store.toolId,
      config: store.config,
      loaded: store.loaded,
    });
    // Gespeicherte Videodauern zurückspielen (Audit H6) — durch dasselbe
    // Index-Mapping wie die Session, und nur für Assets, die in der frischen
    // Config noch ein Cloud-Video haben.
    const storedDurations = stored.video?.durations ?? {};
    const restoredDurations: Record<number, number> = {};
    for (const [k, d] of Object.entries(storedDurations)) {
      const oldIdx = Number(k);
      const newIdx = mapping ? mapping.get(oldIdx) : oldIdx;
      if (newIdx === undefined) continue;
      if (!mergedAssets[newIdx]?.videoPath) continue;
      if (typeof d !== 'number' || !Number.isFinite(d) || d <= 0) continue;
      restoredDurations[newIdx] = d;
    }
    if (Object.keys(restoredDurations).length > 0) {
      useVideoStore.setState((v) => ({ durations: { ...restoredDurations, ...v.durations } }));
    }
    // Restore persisted SFX/music beds BEFORE the orphan cleanup below —
    // the cleanup reads sfxBeds to decide which customDurations still have
    // a duration anchor. (Beds live in their own IDB store; the session
    // record deliberately excludes them.) Bed keys are index-based too, so
    // the same old→new mapping applies.
    try {
      const all = await getAllSfxBeds(persistKey);
      const restored: Record<number, SfxBed[]> = {};
      for (const [idx, rec] of Object.entries(all)) {
        const oldIdx = Number(idx);
        const newIdx = mapping ? mapping.get(oldIdx) : oldIdx;
        if (newIdx === undefined) continue; // asset vanished — drop its beds
        const beds = storedToSfxBeds(rec);
        if (beds.length > 0) restored[newIdx] = beds;
      }
      lastBedsRef.current = restored; // pre-assign: skip the mirror echo
      useSessionStore.setState({ sfxBeds: restored });
    } catch (err) {
      console.error('[session] sfx-bed restore failed', err);
    }
    // Drop any customDurations that were auto-set from a now-missing local
    // video — blob URLs aren't persisted across reloads, so the timeline
    // boundary they anchored is stale. Skips entries whose chips would
    // land past the fallback boundary so we never push existing work into
    // overtime by surprise.
    useSessionStore.getState().cleanupOrphanedCustomDurations();
    // Takes, deren Dauer nie nachgetragen wurde (M17), neu vermessen.
    void repairZeroDurations().catch((err) =>
      console.warn('[session] duration repair failed', err),
    );
  }, []);

  // Hydrate on mount / when persistKey changes / on Retry / Take-over.
  useEffect(() => {
    hydratedRef.current = false;
    lastBedsRef.current = {};
    dirtyRef.current = false;
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (!persistKey) {
      setGate({ kind: 'checking' });
      return;
    }
    setGate({ kind: 'checking' });
    let cancelled = false;
    const steal = stealRef.current;
    stealRef.current = false;

    const markHydrated = () => {
      hydratedRef.current = true;
      requestPersistentStorage();
      setGate({ kind: 'hydrated' });
    };
    const showLocked = () => {
      hydratedRef.current = false;
      setGate({
        kind: 'locked',
        onTakeOver: () => {
          stealRef.current = true;
          // Hat dieser Tab schon einen Stand im Store, wäre der jetzt älter
          // als das, was der andere Tab gespeichert hat — frisch laden.
          if (useSessionStore.getState().name || useSessionStore.getState().sessionFolder) {
            void acquireTabLock(persistKey, true).then(() => window.location.reload());
            return;
          }
          setAttempt((a) => a + 1);
        },
      });
    };

    (async () => {
      const lock = await acquireTabLock(persistKey, steal);
      if (cancelled) {
        lock?.release();
        return;
      }
      if (!lock) {
        showLocked();
        return;
      }
      lockRef.current = lock;
      lock.onStolen(() => {
        if (cancelled) return;
        lockRef.current = null;
        showLocked();
      });

      let loaded: Awaited<ReturnType<typeof loadSession>>;
      try {
        loaded = await loadSession(persistKey);
      } catch (err) {
        if (cancelled) return;
        console.error('[session] hydrate failed', err);
        // Fail-closed: kein Save, solange der alte Stand nicht gelesen
        // werden konnte — sonst überschreibt der leere Store die Session.
        setGate({
          kind: 'error',
          message: formatIDBError(err),
          onRetry: () => setAttempt((a) => a + 1),
        });
        return;
      }
      if (cancelled) return;
      if (!loaded || !hasMeaningfulState(loaded.session)) {
        markHydrated();
        return;
      }
      if (loaded.missing.length > 0) {
        console.warn('[session] audio missing for', loaded.missing);
      }
      const { record, session } = loaded;
      // Stash and ask the user. Hydration waits on their choice so the
      // store doesn't already contain restored state behind the modal.
      const sessionName = session.name?.trim() || 'your session';
      setGate({
        kind: 'restore-prompt',
        sessionName,
        onContinue: () => {
          void applyStored(record, session, persistKey);
          markHydrated();
        },
        onStartFresh: () => {
          void clearSession(persistKey).catch((err) =>
            console.error('[session] clearSession failed', err),
          );
          // Beds live in their own store — drop them with the session,
          // otherwise the next visit would restore beds into a session
          // the user explicitly discarded.
          void deleteAllSfxBeds(persistKey).catch((err) =>
            console.error('[session] sfx-bed clear failed', err),
          );
          markHydrated();
        },
      });
    })();

    return () => {
      cancelled = true;
      lockRef.current?.release();
      lockRef.current = null;
    };
  }, [persistKey, applyStored, attempt]);

  // Subscribe: save on every change after hydration. Videodauern (H6)
  // triggern denselben Save, sobald der Player eine neue meldet.
  useEffect(() => {
    if (!persistKey) return undefined;
    const unsub = useSessionStore.subscribe(scheduleSave);
    const unsubVideo = useVideoStore.subscribe((v, prev) => {
      if (v.durations !== prev.durations) scheduleSave();
    });
    // Tab-Wechsel / Schließen: nicht auf den Timer warten.
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', flush);
    usePersistenceStore.getState().setRetry(flush);
    return () => {
      unsub();
      unsubVideo();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', flush);
      usePersistenceStore.getState().setRetry(null);
      // Letzten Stand nicht verlieren, wenn der Hook abgebaut wird.
      flush();
    };
  }, [persistKey, scheduleSave, flush]);

  // Mirror bed changes into the separate IDB store. Debounced because
  // placement drags fire setBedPlacement per pointermove — writing the
  // multi-MB buffer on every move would hammer IndexedDB. After the timer
  // settles we diff the CURRENT slice against the last-mirrored one and
  // write / delete only the assets that changed.
  useEffect(() => {
    if (!persistKey) return undefined;
    let timer: number | null = null;
    const logErr = (what: string) => (err: unknown) => {
      console.error(`[session] sfx-bed ${what} failed`, err);
      usePersistenceStore.getState().setAutosaveError(formatIDBError(err));
    };
    const mirror = () => {
      timer = null;
      if (!hydratedRef.current) return;
      const cur = useSessionStore.getState().sfxBeds;
      const prev = lastBedsRef.current;
      if (cur === prev) return;
      lastBedsRef.current = cur;
      const seen = new Set<number>();
      for (const [key, beds] of Object.entries(cur)) {
        const idx = Number(key);
        seen.add(idx);
        if (beds === prev[idx]) continue;
        if (beds.length === 0) {
          deleteSfxBeds(persistKey, idx).catch(logErr('delete'));
        } else {
          putSfxBeds(persistKey, idx, sfxBedsToStored(beds)).catch(logErr('save'));
        }
      }
      for (const key of Object.keys(prev)) {
        const idx = Number(key);
        if (!seen.has(idx)) deleteSfxBeds(persistKey, idx).catch(logErr('delete'));
      }
    };
    const unsub = useSessionStore.subscribe((state) => {
      if (!hydratedRef.current) return;
      if (state.sfxBeds === lastBedsRef.current && timer === null) return;
      if (timer !== null) window.clearTimeout(timer);
      timer = window.setTimeout(mirror, 800);
    });
    return () => {
      unsub();
      if (timer !== null) {
        window.clearTimeout(timer);
        // Flush the pending diff so an unmount right after a bed change
        // doesn't drop the write.
        mirror();
      }
    };
  }, [persistKey]);

  return gate;
}
